import React, { useEffect, useState, useCallback } from 'react';
import {
  AppBar as MuiAppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Stack,
  Box,
  Chip,
  Badge,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import StorefrontIcon from '@mui/icons-material/Storefront';
import SyncIcon from '@mui/icons-material/Sync';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AssessmentIcon from '@mui/icons-material/Assessment';

import { useNavigate } from 'react-router-dom';

import OfflineBadge from './OfflineBadge';
import { usePosProfile } from '../pos/PosProfileContext';
import useSession from '../auth/useSession';
import { logout } from '../api/auth';
import { listQueued } from '../offline/outbox';
import { flushOutboxOnce, startSyncTimer } from '../offline/sync';

/**
 * Top application bar for the POS shell.
 * Shows app title, selected profile, user, offline status, sync status, and logout.
 */
export default function PosAppBar({ onCloseShift, onCashMovement, onShiftReport }) {
  const navigate = useNavigate();
  const { profile } = usePosProfile();
  const { user } = useSession();
  const [queuedCount, setQueuedCount] = useState(0);
  const [syncing, setSyncing] = useState(false);

  // Refresh queued count
  const refreshQueue = useCallback(async () => {
    try {
      const q = await listQueued();
      setQueuedCount(q.length);
    } catch {
      // ignore
    }
  }, []);

  // Start sync timer + poll queue count
  useEffect(() => {
    refreshQueue();
    const stopTimer = startSyncTimer(30_000);
    const pollId = setInterval(refreshQueue, 10_000);
    return () => {
      stopTimer();
      clearInterval(pollId);
    };
  }, [refreshQueue]);

  const handleManualSync = async () => {
    setSyncing(true);
    try {
      await flushOutboxOnce();
    } catch {
      // ignore
    } finally {
      await refreshQueue();
      setSyncing(false);
    }
  };

  return (
    <MuiAppBar position="fixed" color="primary" elevation={1}>
      <Toolbar>
        <StorefrontIcon sx={{ mr: 1.5, cursor: 'pointer' }} onClick={() => navigate('/')} />
        <Typography
          variant="h6"
          component="div"
          sx={{ mr: 2, cursor: 'pointer', '&:hover': { opacity: 0.85 } }}
          onClick={() => navigate('/')}
        >
          POS Retail
        </Typography>

        {profile && (
          <Chip
            label={profile.name}
            size="small"
            sx={{
              bgcolor: 'rgba(255,255,255,0.15)',
              color: 'inherit',
              fontWeight: 600,
              mr: 1,
            }}
          />
        )}

        <Box sx={{ flexGrow: 1 }} />

        <Stack direction="row" spacing={1.5} alignItems="center">
          {/* Sync button with queued count badge */}
          <Tooltip title={queuedCount > 0 ? `${queuedCount} pending sync` : 'All synced'}>
            <span>
              <IconButton
                color="inherit"
                size="small"
                onClick={handleManualSync}
                disabled={syncing || queuedCount === 0}
              >
                <Badge badgeContent={queuedCount} color="error" max={99}>
                  {syncing ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <SyncIcon />
                  )}
                </Badge>
              </IconButton>
            </span>
          </Tooltip>

          <OfflineBadge />

          {user && (
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {user}
            </Typography>
          )}

          {onCashMovement && (
            <Button
              color="inherit"
              size="small"
              startIcon={<AccountBalanceWalletIcon />}
              onClick={onCashMovement}
            >
              Cash
            </Button>
          )}

          {onShiftReport && (
            <IconButton color="inherit" size="small" onClick={onShiftReport}>
              <Tooltip title="Shift Report">
                <AssessmentIcon />
              </Tooltip>
            </IconButton>
          )}

          {onCloseShift && (
            <Button
              color="inherit"
              size="small"
              startIcon={<ExitToAppIcon />}
              onClick={onCloseShift}
            >
              Close Shift
            </Button>
          )}

          <Button
            color="inherit"
            size="small"
            startIcon={<LogoutIcon />}
            onClick={logout}
          >
            Logout
          </Button>
        </Stack>
      </Toolbar>
    </MuiAppBar>
  );
}
